// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/Document/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = { 'Access-Control-Allow-Origin': '*' };

// ============= Types =============

export interface DeleteDocumentProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  documentId: string;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Deletes a document from a Data Room
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param documentId Document identifier to delete
 * @returns Promise with API response or error
 */
export const deleteDocument = async ({
  getAccessToken,
  workspaceId,
  documentId,
}: DeleteDocumentProps): Promise<any> => {
  try {
    console.log('🔄 Deleting document...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/document/${documentId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'DELETE',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      const errorText = await response.text();
      console.log(`HTTP error! status: ${response.status}`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log(`✅ Document deleted successfully`);
      return data;
    }

    console.log(`✅ Document deleted successfully`);
    return { success: true };
  } catch (error) {
    console.error('🔴 Error deleting document:', error);
    throw error;
  }
};
